import { appConfig } from '../config/appConfig';
import { apiClient } from './apiClient';

export type FactoryWorkerQueueStatus = {
  alive: boolean;
  enabled?: boolean;
  workerId: string;
  startedAt?: string | null;
  pollMs: number;
  /** Legacy: prep + pipeline capacities summed. */
  jobSlotCapacity: number;
  jobSlotsBusy: number;
  /** 页图准备 lane (layout / ingest / semantic_split / aggregate). */
  prepJobSlotCapacity?: number;
  prepJobSlotsBusy?: number;
  /** extract / enrich / ttq / ipttq lane. */
  pipelineJobSlotCapacity?: number;
  pipelineJobSlotsBusy?: number;
  prepJobTypes?: string[];
  pipelineJobTypes?: string[];
  jobsQueued: number;
  jobsRunningDb: number;
  byTypeQueued?: Record<string, number>;
  byTypeRunning?: Record<string, number>;
  currentJob?: {
    id: string;
    jobType: string;
    batchId: string;
    startedAt: string;
    lane?: string;
  } | null;
  currentJobs?: Array<{
    id: string;
    jobType: string;
    batchId: string;
    startedAt: string;
    lane?: string;
  }>;
  jobsClaimedTotal?: number;
  extractPageConcurrency: number;
  claimJobTypes?: string[];
};

export type FactoryBatch = {
  id: string;
  title: string;
  status: string;
  strategyTier: string;
  errorMessage?: string;
  meta?: Record<string, unknown>;
  enableSemanticSplit?: boolean;
  layoutPhase?: string | null;
  pipelineRestartedAt?: string | null;
  primaryDiscipline?: string | null;
  extractEnabled?: boolean | null;
  disciplineStatus?: string | null;
  disciplineNote?: string | null;
  disciplineResampleHint?: string | null;
  mobile2Subject?: string | null;
  processingAuthorization?: string;
  sourceMaterialId?: string | null;
  ownedByAccountId?: string | null;
  orderId?: string | null;
  warehouseStatus?: string;
  releaseAuthorization?: string | null;
  goodsReadyAt?: string | null;
  ownerAcceptedAt?: string | null;
  ownerAccepted?: boolean;
  pipelinePaused?: boolean;
  /** ISO time when Owner/Worker paused the pipeline (freeze wall clocks). */
  pipelinePausedAt?: string | null;
  qcBy?: string | null;
  qcAt?: string | null;
  qcNote?: string;
  createdAt: string;
  updatedAt: string;
  /** List enrichment from GET /batches */
  currentPhase?: string | null;
  currentPhaseLabel?: string | null;
  progressPct?: number | null;
  progressLabel?: string | null;
  phaseStatus?: string | null;
  /** ISO time of driving Job last progress update (list stall hint). */
  jobUpdatedAt?: string | null;
  /** Rough CNY cost (Qwen3.7-Plus tokens + FC CU × 1.2). */
  costEstimate?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    estimatedFcCalls: number;
    estimatedFcCu: number;
    fcRateCnyPerCu: number;
    fcInstanceVcpu: number;
    fcInstanceMemGb: number;
    tokenCny: number;
    fcCny: number;
    totalCny: number;
    margin: number;
    currency: 'CNY';
    model: 'qwen3.7-plus';
    incomplete: boolean;
  } | null;
  costEstimateLabel?: string | null;
};

export type FactoryCostLedger = {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedFcCalls: number;
  estimatedFcCu: number;
  fcRateCnyPerCu: number;
  fcInstanceVcpu: number;
  fcInstanceMemGb: number;
  tokenCny: number;
  fcCny: number;
  totalCny: number;
  margin: number;
  currency: 'CNY';
  model: 'qwen3.7-plus';
  incomplete: boolean;
  storageBytes: number;
  storageGb: number;
  storageCny: number;
  storageMargin: number;
  storageRateCnyPerGbMonth: number;
  trafficGb: number;
  trafficCny: number;
  trafficMargin: number;
  trafficFetchFactor: number;
  trafficRateCnyPerGb: number;
  batchCount: number;
  pageCount: number;
  computeCny: number;
  grandTotalCny: number;
  perPageCny: number | null;
};

export type FactoryBatchPhaseId =
  | 'semantic_split'
  | 'layout'
  | 'structure'
  | 'extract'
  | 'figure_extract'
  | 'ttq'
  | 'ipttq';

export type FactoryBatchPhase = {
  phase: FactoryBatchPhaseId | string;
  status: string;
  progressPct?: number;
  progressLabel?: string;
  wired?: boolean;
};

/** Per-unit averages from GET /batches/:id/usage */
export type FactoryPhaseMetric = {
  key: 'layout' | 'extract' | 'ttq' | 'ipttq' | string;
  label: string;
  unitLabel: string;
  started: boolean;
  status: string | null;
  unitCount: number;
  unitDone?: number | null;
  secondaryCount?: number | null;
  secondaryLabel?: string | null;
  extraLabel?: string | null;
  pressureActual?: number | null;
  pressureTarget?: number | null;
  pressureLabel?: string | null;
  pressureNeedsManual?: boolean;
  latencySamples?: number;
  avgLatencyMs: number | null;
  avgTokens: number | null;
  totalTokens: number;
};

/** @deprecated Prefer FactoryBatchPhase */
export type FactoryJobFacet = {
  jobType: string;
  status: string;
  progressPct?: number;
  progressLabel?: string;
  wired?: boolean;
};

export type FactoryAsset = {
  id: string;
  batchId: string;
  filename: string;
  contentType: string;
  byteSize: number;
  contentSha256: string;
  ossKey: string;
  pageCount: number;
  status: string;
};

export type FactoryJob = {
  id: string;
  batchId: string;
  jobType: string;
  status: string;
  progressPct: number;
  progressLabel: string;
  errorMessage?: string;
  result?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

export type FactoryBatchDetail = {
  batch: FactoryBatch;
  assets: FactoryAsset[];
  jobs: FactoryJob[];
  phases?: FactoryBatchPhase[];
  currentPhase?: FactoryBatchPhaseId | string;
  facets?: FactoryJobFacet[];
  pageCount: number;
  artifactAccess?: boolean;
  message?: string;
};

export type FactoryLayoutUnit = {
  id: string;
  assetId: string;
  unitIndex: number;
  pageStart: number;
  pageEnd: number;
  mineruStatus: string;
  pageCount: number;
  byteSize?: number;
  errorMessage?: string | null;
  hasLayout?: boolean;
};

export type FactoryLayoutContentItem = {
  item_id?: string;
  type?: string;
  text?: string;
  bbox?: [number, number, number, number];
  page_idx?: number;
  img_path?: string;
  _originalIndex?: number;
  _globalIndex?: number;
};

/** block-stream / extras-compatible extract precursor (AppFactory aggregate output). */
export type FactoryBlockStreamExtractPrecursor = {
  schema?: string;
  target_fc?: string;
  input_kind?: string;
  unit_id?: string;
  page_span?: number[];
  blocks?: Array<{
    id?: string;
    type?: string;
    page?: number | null;
    text?: string;
    caption?: string;
    img_url?: string | null;
    bbox?: number[] | null;
  }>;
  images?: Array<{
    id?: string;
    page?: number | null;
    role?: string;
    image_url?: string | null;
    resolve?: Record<string, unknown>;
  }>;
  mode?: string;
};

export type FactoryStructuralUnit = {
  id: string;
  /** 归属工厂任务；列表/插图台必须与当前 batch 一致 */
  batchId?: string | null;
  unitIndex: number;
  pageSpan: number[] | unknown;
  readingOrder?: unknown;
  answeredHint?: string;
  status: string;
  textPreview: string;
  itemCount: number;
  layoutUnitId?: string | null;
  extractTarget?: string | null;
  structurePhase?: string | null;
  extractPrecursor?: FactoryBlockStreamExtractPrecursor | null;
  extractStatus?: string | null;
  extractSummary?: {
    qaCount?: number;
    title?: string;
    route?: string;
    model?: string | null;
    latencyMs?: number | null;
    usage?: {
      prompt_tokens?: number;
      completion_tokens?: number;
      total_tokens?: number;
    } | null;
  } | null;
  extractError?: string | null;
  /** Worker marked blank/junk page; excluded from extractFailed. */
  extractIgnored?: boolean;
  /**
   * Complete QA preview (stem/answer/subs, no truncation).
   * Prefer this over parsing extractResult when present (list ?view=preview).
   */
  extractQa?: Array<{
    question: string;
    answer: string;
    subQuestions?: string[];
    subAnswers?: string[];
    confidence?: number;
    hasProblemFigure?: boolean;
    figureKind?: string;
    figureNote?: string;
    figureBox?: { x: number; y: number; w: number; h: number };
    figureSource?: 'extract' | 'inferred' | 'missing';
  }> | null;
  extractTitle?: string | null;
  /** block-stream FC payload stored on unit after extract. */
  extractResult?: {
    success?: boolean;
    analysis?: unknown;
    metadata?: unknown;
    unit?: unknown;
    particles?: unknown;
    extractedAt?: string;
  } | null;
  enrichStatus?: string | null;
  enrichSummary?: {
    qaCount?: number;
    rfeBound?: number;
    papReady?: number;
    profilesProvided?: boolean;
    profileId?: string | null;
    gradeBand?: string | null;
    gradeBandSource?: string | null;
    rfeCascade?: string | null;
    learnerAgeYears?: number | null;
    /** From loadFactoryEnrichContext — e.g. runtime_admin_token_missing */
    contextWarnings?: string[] | null;
  } | null;
  enrichError?: string | null;
  /** Per-QA RFE + PAP payloads after enrich. */
  enrichResult?: {
    success?: boolean;
    subjectCode?: string;
    particles?: Array<{
      index?: number;
      rfe?: Record<string, unknown> | null;
      pap?: Record<string, unknown> | null;
      rfeWarnings?: unknown;
      papWarnings?: unknown;
      models?: unknown;
    }>;
    enrichedAt?: string;
  } | null;
  /** Page coach-overlay FC result (unit.meta.coachOverlay). */
  coachOverlay?: Record<string, unknown> | null;
  coachOverlayStatus?: string | null;
  coachOverlayError?: string | null;
  /** Page learner-evidence FC result (unit.meta.learnerEvidence); UI label: learner-overlay. */
  learnerEvidence?: Record<string, unknown> | null;
  learnerEvidenceStatus?: string | null;
  learnerEvidenceError?: string | null;
  /** List uses preview; GET unit returns full. */
  view?: 'preview' | 'full';
  contentItems?: FactoryLayoutContentItem[];
  regions?: Array<{
    page_idx?: number;
    bbox?: [number, number, number, number];
    type?: string;
    text?: string;
    item_id?: string;
    _originalIndex?: number;
  }>;
};

/** Factory warehouse 品题 — isomorphic with learning AdmissionRecommendation / Ttqa draft. */
export type FactoryTtqAdmissionRecommendation = {
  id: string;
  userId: string;
  subjectCode: 'math' | 'yuwen';
  sourceParticleId: string;
  action: 'join_existing_ttqa' | 'open_new_ttqa';
  targetTtqaId: string | null;
  suggestedPeerIds: string[];
  scores: {
    trunkProximity: number;
    typeHomogeneity: number;
    implicitAbility: number;
    mathStructure: number;
    total: number;
  };
  ringEvidence?: {
    ring: 0 | 1 | 2;
    sharedAbilityKeys?: string[];
    sharedExamPointLabels?: string[];
    notes?: string[];
  };
  status: 'recommended' | 'approved' | 'rejected' | 'superseded';
  createdAt: string;
  updatedAt?: string;
};

export type FactoryWarehouseTtqaParticle = {
  id: string;
  particleId: string;
  ordinal: number;
  particleTitle?: string | null;
  particleQuestions?: string | null;
  particleAnswers?: string | null;
  subQuestions?: string[];
  subAnswers?: string[];
  pipelineStatus?: string;
  pap?: Record<string, unknown> | null;
};

export type FactoryWarehouseTtqa = {
  id: string;
  userId: string;
  subjectCode: 'math' | 'yuwen';
  status: 'draft' | 'active' | 'completed';
  title?: string | null;
  anchorParticleId?: string | null;
  particles: FactoryWarehouseTtqaParticle[];
  factoryMeta?: {
    warehouseStatus?: string;
    learningAdmitted?: boolean;
    admissionRecommendationId?: string | null;
    productId?: string | null;
    disposition?: 'created' | 'joined' | 'critical' | null;
    memberCap?: number | null;
  };
};

export type FactoryTtqProduct = {
  id: string;
  accountId: string;
  ownedByUserId: string;
  subjectCode: 'math' | 'yuwen';
  title: string;
  thinkingLineage: string;
  memberCap: number;
  skill: Record<string, unknown>;
  bankDrafts: unknown[];
  status: 'active' | 'archived';
  sourceBatchId: string | null;
  memberBatchIds?: string[];
  meta: Record<string, unknown>;
  memberCount: number;
  recycleCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type FactoryTtqProductMember = {
  id: string;
  productId: string;
  particleId: string;
  batchId: string | null;
  ordinal: number;
  membershipRole?: 'induction' | 'recycle';
  snapshot: Record<string, unknown>;
  addedAt: string;
};

export type FactoryCriticalZoneItem = {
  id: string;
  accountId: string;
  ownedByUserId: string;
  subjectCode: 'math' | 'yuwen';
  productId: string | null;
  batchId: string | null;
  reason: 'flash_reject' | 'over_cap';
  status: 'pending' | 'approved' | 'rejected';
  candidate: Record<string, unknown>;
  flash: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type FactoryTtqAdmissionResult = {
  schemaVersion?: number;
  batchId?: string;
  ownedByUserId?: string | null;
  subjectCode?: 'math' | 'yuwen';
  particleCount: number;
  extractParticleCount?: number;
  recycleParticleCount?: number;
  reuseSeatCount?: number;
  coverageBoundCount?: number;
  recommendationCount: number;
  warehouseTtqaCount: number;
  recommendations: FactoryTtqAdmissionRecommendation[];
  warehouseTtqas: FactoryWarehouseTtqa[];
  builtAt: string;
  warnings?: string[];
};

export type FactoryTtqAdmissionResponse = {
  success: boolean;
  batchId: string;
  builtAt: string | null;
  ttqAdmission: FactoryTtqAdmissionResult | null;
  summary: {
    particleCount: number;
    recommendationCount: number;
    warehouseTtqaCount: number;
    skipped?: boolean;
  };
};

/** LLM similarity re-admit report (batch.meta.ttqSimilarityReadmitReport). */
export type SimilarityReAdmitReport = {
  merged: number;
  considered: number;
  skippedReason?: string | null;
  sampleRejectReasons?: string[];
  mergedPairs?: Array<{
    fromId: string;
    intoId: string;
    fromTitle: string;
    intoTitle: string;
    reason: string;
  }>;
  finishedAt?: string;
};

/** Factory warehouse IPTTQ — isomorphic with learning IpttqRecord (drafts; no drip). */
export type FactoryIpttqSkillSpec = {
  mainTitle: string;
  subtitle?: string | null;
  implicitCapabilities?: string[];
  thinkingLineage?: string;
  answerMustShow?: string[];
  forbiddenExplicitLabels?: string[];
  notes?: string | null;
  intuitionSketch?: { status?: string } | null;
  contentUpdatedAt?: string | null;
  generatorModel?: string | null;
  generatorProvider?: string | null;
  schemaVersion?: 1;
};

export type FactoryInducedKpDraft = {
  id: string;
  title: string;
  questions: { main: string; subQuestions?: string[] };
  answers: { main: string; subAnswers?: string[] };
  metadata?: {
    displayTitle?: string;
    ipttq?: { ipttqId: string; role: 'induced' };
    factoryDraft?: boolean;
    learningAdmitted?: boolean;
    createdAt?: string;
    generateMs?: number;
    generateStartedAt?: string;
    inductionMode?: 'member-sample' | 'corpus-segment';
    referenceSampleSize?: number;
    sampledMemberIndices?: number[];
    sampledParticleIds?: string[];
    sampledMemberTitles?: string[];
    corpusSegmentId?: string;
    corpusTheme?: string;
  };
};

export type FactoryIpttqRecord = {
  id: string;
  ttqaId: string;
  userId: string;
  skill: FactoryIpttqSkillSpec;
  referenceParticleIds?: string[];
  referenceCttqIds?: string[];
  bankMode?: 'generated';
  bankParticleIds?: string[];
  bankParticleDrafts?: FactoryInducedKpDraft[];
  effectiveBankCount?: number;
  lifecycle?: 'draft' | 'active' | 'archived';
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  factoryMeta?: {
    warehouseTtqaTitle?: string | null;
    skillOk?: boolean;
    bankOk?: boolean;
    targetBank?: number;
    /** Native induction members (excludes cross-product reuse seats). */
    nativeMemberCount?: number;
    totalMemberCount?: number;
    warnings?: string[];
    learningAdmitted?: boolean;
  };
};

export type FactoryIpttqBatchResult = {
  schemaVersion?: number;
  batchId?: string;
  subjectCode?: 'math' | 'yuwen';
  warehouseTtqaCount: number;
  ipttqCount: number;
  bankDraftCount: number;
  targetBank: number;
  records: FactoryIpttqRecord[];
  builtAt: string;
  warnings?: string[];
};

export type ProductBankStockRunState = {
  sessionId: string;
  status: 'running' | 'done' | 'failed' | 'locked' | 'paused';
  startedAt: string;
  updatedAt: string;
  finishedAt?: string;
  phase?: 'plan' | 'corpus' | 'bank' | 'done' | 'skip';
  bankDone?: number;
  bankTarget?: number;
  inFlight?: number;
  timeouts?: number;
  skips?: number;
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number } | null;
  error?: string;
  added?: number;
  skippedLocked?: boolean;
};

export type ProductBankStockStatusResponse = {
  success: boolean;
  product: {
    id: string;
    title: string;
    bankDrafts: FactoryInducedKpDraft[];
    memberCount: number;
    meta: Record<string, unknown>;
  };
  run: ProductBankStockRunState | null;
  bankCount: number;
  elapsedMs: number;
  resolvedBankTarget?: number;
};

export type FactoryIpttqResponse = {
  success: boolean;
  batchId: string;
  builtAt: string | null;
  ipttq: FactoryIpttqBatchResult | null;
  summary: {
    ipttqCount: number;
    bankDraftCount: number;
    warehouseTtqaCount: number;
    targetBank: number;
    skipped?: boolean;
  };
};

export type FactoryShelfMaterial = {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  contentSha256: string;
  ossKey: string;
  title: string;
  status: string;
  batchId?: string | null;
  materialCode?: string;
  ownedByAccountId?: string | null;
  ownedByUserId?: string | null;
  ownedByDisplayName?: string | null;
  uploadedByAccountId?: string | null;
  uploadedByUserId?: string | null;
  uploadedByDisplayName?: string | null;
  orderId?: string | null;
  pageCount?: number | null;
  createdAt: string;
  updatedAt: string;
};

/** Batch progress attached to Owner orders (/orders/mine). */
export type FactoryOrderBatchProgress = {
  id: string;
  title: string;
  status: string;
  processingAuthorization?: string;
  orderId?: string | null;
  warehouseStatus?: string;
  releaseAuthorization?: string | null;
  goodsReadyAt?: string | null;
  ownerAcceptedAt?: string | null;
  ownerAccepted?: boolean;
  primaryDiscipline?: string | null;
  extractEnabled?: boolean | null;
  currentPhase?: string | null;
  phases?: FactoryBatchPhase[];
  errorMessage?: string | null;
  createdAt: string;
  updatedAt?: string;
};

export type FactoryOrderIntake = {
  intent?: string | null;
  services?: string[] | null;
  pageCount?: number | null;
  contactName?: string | null;
  phone?: string | null;
  address?: string | null;
  street?: string | null;
  region?: string[] | null;
  photos?: Array<{ assetId?: string; ossId?: string }> | null;
  tokenRateYuan?: number | null;
  visitDepositFen?: number | null;
  estimatedFeeFen?: number | null;
  depositFen?: number | null;
  title?: string | null;
};

export type FactoryOrder = {
  id: string;
  orderCode: string;
  title: string;
  status: string;
  ownerAccountId: string;
  ownerUserId?: string | null;
  ownerDisplayName?: string | null;
  preferredWorkerUserId?: string | null;
  preferredWorkerAccountId?: string | null;
  assigneeUserId?: string | null;
  assigneeAccountId?: string | null;
  assigneeDisplayName?: string | null;
  claimedAt?: string | null;
  note?: string;
  intake?: FactoryOrderIntake | Record<string, unknown> | null;
  paymentStatus?: string | null;
  billedPageCount?: number | null;
  depositFen?: number | null;
  paidFen?: number | null;
  refundedFen?: number | null;
  balanceDueFen?: number | null;
  depositCoveredPages?: number | null;
  actualPageCount?: number | null;
  batches?: FactoryOrderBatchProgress[];
  createdAt: string;
  updatedAt: string;
};

export type FactoryReleaseRequest = {
  id: string;
  batchId: string;
  accountId: string;
  ownedByAccountId?: string | null;
  status: string;
  requestedBy: string;
  functorReceipt?: string;
  createdAt: string;
  updatedAt: string;
};

function stripHtmlNoise(raw: string): string {
  const text = String(raw || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text;
}

function friendlyError(payload: Record<string, unknown>, status: number): string {
  const err = String(payload.error || '');
  const msg = String(payload.message || '');
  const combined = stripHtmlNoise(`${err} ${msg}`);
  if (err === 'token_expired' || /token.?expired|访问令牌已过期/i.test(msg)) {
    return '登录已过期，请重新登录';
  }
  if (/Cannot POST\s+\/api\/batches\/[^/]+\/restart-pipeline/i.test(combined)) {
    return '工厂服务尚未包含「重新启动全部工序」接口，请重新部署 AppFactory 后再试';
  }
  if (/^Cannot (GET|POST|PUT|PATCH|DELETE)\b/i.test(combined) || /<!DOCTYPE html/i.test(msg)) {
    return `工厂服务接口不可用（HTTP ${status}）。若刚加了新接口，请确认 AppFactory 已重新部署。`;
  }
  if (status === 413) return msg || '文件过大';
  if (status === 409 || err === 'duplicate_content') return msg || '当前账户下已有相同文件';
  if (err === 'factory_worker_required') return msg || '需要已批准的工厂工作人员能力';
  if (err === 'judge_discipline_failed') {
    return msg || '学科判定失败，请稍后重试';
  }
  if (err === 'material_code_required' || err === 'material_code_invalid') {
    return msg || '请提供已绑定日记账户的素材码';
  }
  if (err === 'order_or_material_code_required' || err === 'order_not_found' || err === 'order_not_claimed') {
    return msg || '请先接单并绑定工厂订单后再上架';
  }
  if (err === 'preferred_worker_required' || err === 'preferred_worker_invalid') {
    return msg || '请选择已批准的工厂工作人员账户';
  }
  if (err === 'not_preferred_worker') return msg || '该订单已指定其他工作人员';
  if (err === 'not_cancellable') return msg || '已进厂开始加工，无法取消，定金不退';
  if (err === 'not_paid') return msg || '定金尚未支付，不能进厂';
  if (err === 'page_quota_exceeded') return msg || '将超出定金覆盖页量，请先向用户发起补充页量订单';
  if (err === 'not_over_quota') return msg || '尚未超出定金覆盖页量，无需补页';
  if (err === 'owner_order_limit') return msg || '每人最多同时保留 3 个进行中的订单';
  if (err === 'worker_order_limit') return msg || '每位工作人员同时只能接 1 个订单';
  if (err === 'not_abandonable' || err === 'processing_started') {
    return msg || '已进厂开始加工，无法放弃';
  }
  if (err === 'owner_authorization_required') return msg || '等待 Owner 授权后才能继续加工';
  if (msg && !msg.trim().startsWith('{') && !/<!DOCTYPE|<html/i.test(msg)) return msg;
  if (err && !/<!DOCTYPE|<html/i.test(err)) return err;
  return `工厂服务 HTTP ${status}`;
}

export function isFactoryWorkerRequiredError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || '');
  return /需要已批准的工厂工作人员能力|factory_worker_required/i.test(message);
}

/** Factory desk traffic must stay on AppFactory (ECS). Never poll FC / fcapp.run. */
function assertFactoryApiTarget(baseUrl: string): void {
  const base = String(baseUrl || '').trim();
  if (!base) throw new Error('未配置 AppFactory 基址（VITE_APP_FACTORY_BASE_URL）');
  if (/fcapp\.run/i.test(base) || /\.fc\.aliyuncs\.com/i.test(base)) {
    throw new Error('禁止工厂产线轮询/请求 Function Compute 地址；请指向 AppFactory');
  }
}

async function factoryFetch<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs = 60_000,
): Promise<T> {
  assertFactoryApiTarget(appConfig.appFactoryBaseUrl);

  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('未登录，无法访问颗粒工厂');

  const headers = new Headers(init.headers || {});
  headers.set('Accept', 'application/json');
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${appConfig.appFactoryBaseUrl}${path}`, {
      ...init,
      headers,
      signal: controller.signal,
    });
    const text = await res.text();
    let payload: Record<string, unknown> = {};
    try {
      payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      payload = { message: text };
    }
    if (!res.ok) throw new Error(friendlyError(payload, res.status));
    return payload as T;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`连接颗粒工厂超时（${appConfig.appFactoryBaseUrl}${path}）`);
    }
    if (error instanceof TypeError || /failed to fetch/i.test(String((error as Error)?.message || error))) {
      throw new Error(
        `无法连接颗粒工厂 ${appConfig.appFactoryBaseUrl}${path}（网络中断或服务重启中）`,
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export const factoryStatusLabel: Record<string, string> = {
  draft: '草稿',
  uploading: '上传中',
  queued: '排队中',
  processing: '处理中',
  layout_done: '版面就绪',
  ready: '就绪',
  failed: '失败',
  cancelled: '已取消',
};

export const factoryOrderStatusLabel: Record<string, string> = {
  open: '待接单',
  claimed: '已接单',
  in_progress: '加工中',
  releasing: '释放中',
  after_sales: '售后',
  closed: '已关闭',
  cancelled: '已取消',
};

export const factoryAuthLabel: Record<string, string> = {
  none: '未请求授权',
  awaiting_owner_auth: '待你授权',
  factory: '已授权工厂',
  owner_self: '自行处理',
  rejected: '已拒绝',
};

export const factoryPhaseLabel: Record<string, string> = {
  layout: '版面拆分',
  semantic_split: '语义切分',
  structure: '结构聚合',
  extract: '颗粒提取',
  ttq: '品题推荐',
};

export type FactorySemanticSplitLine = {
  y_norm: number;
  confidence?: number | null;
  reason?: string;
};

export type FactorySemanticSplitPage = {
  id: string;
  pageIndex: number;
  width: number;
  height: number;
  lines: FactorySemanticSplitLine[];
  model: string;
  usage: Record<string, unknown>;
  latencyMs: number;
  hasImage: boolean;
};

export type FactorySemanticSplitsResult = {
  pages: FactorySemanticSplitPage[];
  summary: {
    pageCount: number;
    lineCount: number;
    model: string;
    totalPromptTokens: number;
    totalCompletionTokens: number;
    totalLatencyMs: number;
  };
};

export const factoryPhaseStatusLabel: Record<string, string> = {
  pending: '等待前序',
  planned: '计划中',
  blocked_by_auth: '待授权',
  queued: '排队中',
  running: '运行中',
  succeeded: '已完成',
  failed: '失败',
  not_wired: '待接入',
  awaiting_discipline: '待学科判定',
  skipped_unsupported_discipline: '非数学·暂跳过',
  skipped_optional: '未启用',
};

/** Headline progress for an Owner order (auth / phase over coarse order status). */
export function factoryOrderProgressLabel(order: FactoryOrder): string {
  const batches = order.batches || [];
  if (!batches.length) return factoryOrderStatusLabel[order.status] || order.status;
  if (batches.some((b) => b.processingAuthorization === 'awaiting_owner_auth')) {
    return '待你授权加工';
  }
  if (batches.some((b) => b.processingAuthorization === 'rejected')) {
    return '已拒绝加工';
  }
  if (batches.some((b) => b.releaseAuthorization === 'authorized') || order.status === 'releasing') {
    return '释放中 · 可停止';
  }
  if (
    batches.some(
      (b) =>
        b.releaseAuthorization === 'awaiting_owner_auth'
        || (b.status === 'ready'
          && b.releaseAuthorization !== 'authorized'
          && b.releaseAuthorization !== 'rejected'),
    )
  ) {
    return '成品智能体就绪 · 可开始释放';
  }
  if (order.status === 'after_sales') {
    return factoryOrderStatusLabel[order.status] || order.status;
  }
  const focus =
    batches.find((b) => b.processingAuthorization === 'factory' || b.processingAuthorization === 'owner_self')
    || batches[batches.length - 1]!;
  const auth = factoryAuthLabel[focus.processingAuthorization || ''] || '';
  const phaseName = factoryPhaseLabel[focus.currentPhase || ''] || '';
  const phaseRow = focus.phases?.find((p) => p.phase === focus.currentPhase);
  const phaseSt = phaseRow ? factoryPhaseStatusLabel[phaseRow.status] || phaseRow.status : '';
  if (auth && phaseName) {
    return `${auth} · ${phaseName}${phaseSt ? `（${phaseSt}）` : ''}`;
  }
  if (auth) return auth;
  return factoryOrderStatusLabel[order.status] || order.status;
}

const DEFAULT_FACTORY_ORDER_TITLE = '未命名工厂订单';

/** Prefer note as headline when title is the default placeholder. */
export function factoryOrderDisplayTitle(order: Pick<FactoryOrder, 'title' | 'note'>): string {
  const title = String(order.title || '').trim();
  const note = String(order.note || '').trim();
  if (note && (!title || title === DEFAULT_FACTORY_ORDER_TITLE)) return note;
  return title || note || DEFAULT_FACTORY_ORDER_TITLE;
}

export function factoryOrderOwnerLabel(
  order: Pick<FactoryOrder, 'ownerDisplayName' | 'ownerUserId' | 'ownerAccountId'>,
): string {
  const name = String(order.ownerDisplayName || '').trim();
  if (name) return name;
  const userId = String(order.ownerUserId || '').trim();
  if (userId) return `${userId.slice(0, 8)}…`;
  const accountId = String(order.ownerAccountId || '').trim();
  if (accountId) return `${accountId.slice(0, 8)}…`;
  return '未知';
}

export function factoryOrderCreatedLabel(order: Pick<FactoryOrder, 'createdAt'>): string {
  const t = Date.parse(String(order.createdAt || ''));
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function factoryOrderSecondaryNote(order: Pick<FactoryOrder, 'title' | 'note'>): string {
  const title = String(order.title || '').trim();
  const note = String(order.note || '').trim();
  if (!note) return '';
  // Note already shown as the main title — don't repeat.
  if (!title || title === DEFAULT_FACTORY_ORDER_TITLE) return '';
  return note;
}

/** Header / chip: 订单码 + 订单主. */
export function factoryOrderScopeLabel(
  order: Pick<FactoryOrder, 'orderCode' | 'ownerDisplayName' | 'ownerUserId' | 'ownerAccountId'>,
): string {
  const code = String(order.orderCode || '').trim();
  const owner = factoryOrderOwnerLabel(order);
  return code ? `${code} · 订单主 ${owner}` : `订单主 ${owner}`;
}

export function isFactoryBatchAwaitingRelease(batch: {
  status?: string;
  orderId?: string | null;
  releaseAuthorization?: string | null;
}): boolean {
  // Explicit Worker → Owner handoff only (pipeline ready alone is not enough).
  return batch.releaseAuthorization === 'awaiting_owner_auth';
}

export type FactoryOrderAttentionKind = 'auth' | 'releasing' | 'release';

/** 批次仍在工厂加工管线中（含待授权），不应算「已完成」。 */
export function isFactoryBatchInActiveProcessing(batch: {
  status?: string;
  processingAuthorization?: string | null;
  releaseAuthorization?: string | null;
}): boolean {
  if (batch.processingAuthorization === 'awaiting_owner_auth') return true;
  if (batch.processingAuthorization === 'rejected') return false;
  if (batch.releaseAuthorization === 'authorized') return false;
  if (isFactoryBatchAwaitingRelease(batch)) return false;
  const status = String(batch.status || '').trim();
  if (status === 'ready' || status === 'cancelled' || status === 'failed') return false;
  return Boolean(status);
}

export function factoryOrderHasActiveProcessing(order: FactoryOrder): boolean {
  return (order.batches || []).some((b) => isFactoryBatchInActiveProcessing(b));
}

export function factoryOrderAttentionKind(order: FactoryOrder): FactoryOrderAttentionKind | null {
  const batches = order.batches || [];
  if (batches.some((b) => b.processingAuthorization === 'awaiting_owner_auth')) return 'auth';
  if (factoryOrderHasActiveProcessing(order)) return null;
  if (batches.some((b) => b.releaseAuthorization === 'authorized') || order.status === 'releasing') {
    return 'releasing';
  }
  if (batches.some((b) => isFactoryBatchAwaitingRelease(b))) return 'release';
  return null;
}

export function factoryOrderNeedsAttention(order: FactoryOrder): boolean {
  return factoryOrderAttentionKind(order) != null;
}

export type FactoryOrderListFilter = 'all' | 'active' | 'done' | 'cancelled';

/**
 * 成品智能体已构建完成（含待释放 / 释放中 / 售后），归入「已完成」。
 * 若同单仍有批次在加工（例如追加新任务），不算完成 → 回到「进行中」。
 */
export function factoryOrderIsGoodsComplete(order: FactoryOrder): boolean {
  if (factoryOrderHasActiveProcessing(order)) return false;
  if (
    order.status === 'releasing'
    || order.status === 'after_sales'
    || order.status === 'closed'
  ) {
    return true;
  }
  const batches = order.batches || [];
  return batches.some(
    (b) =>
      b.status === 'ready'
      || isFactoryBatchAwaitingRelease(b)
      || b.releaseAuthorization === 'authorized',
  );
}

/** 进行中 = 工厂仍在加工；已完成 = 成品智能体就绪及之后（含释放）。 */
export function factoryOrderFilterBucket(
  order: FactoryOrder,
): Exclude<FactoryOrderListFilter, 'all'> {
  if (order.status === 'cancelled') return 'cancelled';
  if (factoryOrderIsGoodsComplete(order)) return 'done';
  return 'active';
}

/** 进行中卡片：同步工厂当前工序。 */
export function factoryOrderFactoryStepLabel(order: FactoryOrder): string {
  const batches = order.batches || [];
  if (!batches.length) {
    return factoryOrderStatusLabel[order.status] || order.status;
  }
  if (batches.some((b) => b.processingAuthorization === 'awaiting_owner_auth')) {
    return '待你授权加工';
  }
  if (batches.some((b) => b.processingAuthorization === 'rejected')) {
    return '已拒绝加工';
  }
  const focus =
    batches.find(
      (b) => b.processingAuthorization === 'factory' || b.processingAuthorization === 'owner_self',
    ) || batches[batches.length - 1]!;
  const phaseName = factoryPhaseLabel[focus.currentPhase || ''] || focus.currentPhase || '';
  const phaseRow = focus.phases?.find((p) => p.phase === focus.currentPhase);
  const phaseSt = phaseRow ? factoryPhaseStatusLabel[phaseRow.status] || phaseRow.status : '';
  if (phaseName) {
    return phaseSt ? `${phaseName}（${phaseSt}）` : phaseName;
  }
  return factoryOrderStatusLabel[order.status] || order.status;
}

export type FactoryReleaseUiState = {
  batch: FactoryOrderBatchProgress;
  mode: 'awaiting' | 'releasing';
};

export function isFactoryBatchOwnerAccepted(batch: {
  ownerAccepted?: boolean;
  ownerAcceptedAt?: string | null;
}): boolean {
  return Boolean(batch.ownerAccepted || batch.ownerAcceptedAt);
}

/** 已验收 / 释放中：货架只消费，不再补传或改绑定。 */
export function isFactoryOrderShelfOpen(order: FactoryOrder): boolean {
  if (order.status !== 'claimed' && order.status !== 'in_progress') return false;
  if (factoryOrderHasActiveProcessing(order)) return true;
  if ((order.batches || []).some((b) => isFactoryBatchOwnerAccepted(b))) return false;
  if (factoryOrderIsGoodsComplete(order)) return false;
  return true;
}

/** 已完成卡片：取可开始/停止释放的批次（优先释放中）。有加工中批次时不展示。 */
export function factoryOrderReleaseUi(order: FactoryOrder): FactoryReleaseUiState | null {
  if (factoryOrderHasActiveProcessing(order)) return null;
  const batches = order.batches || [];
  const releasing = batches.find((b) => b.releaseAuthorization === 'authorized');
  if (releasing) return { batch: releasing, mode: 'releasing' };
  const awaiting = batches.find((b) => isFactoryBatchAwaitingRelease(b));
  if (awaiting) return { batch: awaiting, mode: 'awaiting' };
  return null;
}

export function sortFactoryOrdersByTime(a: FactoryOrder, b: FactoryOrder): number {
  return String(b.updatedAt || b.createdAt).localeCompare(String(a.updatedAt || a.createdAt));
}

export function factoryOrderAttentionHint(order: FactoryOrder): string | null {
  const kind = factoryOrderAttentionKind(order);
  if (kind === 'auth') return '待授权加工';
  if (kind === 'releasing') return '颗粒释放中';
  if (kind === 'release') return '颗粒待释放';
  return null;
}

export type FactoryReleasePreviewDraft = {
  id: string;
  title: string;
  questions: { main: string; subQuestions?: string[] };
  answers: { main: string; subAnswers?: string[] };
  metadata?: Record<string, unknown>;
};

export type FactoryReleasePreviewProduct = {
  id: string;
  batchId: string;
  title: string;
  mainTitle: string;
  thinkingLineage: string;
  implicitCapabilities: string[];
  bankDrafts: FactoryReleasePreviewDraft[];
};

export type FactoryOrderReleasePreview = {
  order: FactoryOrder;
  products: FactoryReleasePreviewProduct[];
  summary: {
    productCount: number;
    particleCount: number;
    batchCount: number;
  };
};

export const factoryService = {
  baseUrl: () => appConfig.appFactoryBaseUrl,

  /** Ungated liveness probe (no Bearer). Used by FactoryShell before /api/shelf. */
  async probeHealth(timeoutMs = 8_000): Promise<{
    ok: boolean;
    status: number;
    bodyPreview: string;
  }> {
    assertFactoryApiTarget(appConfig.appFactoryBaseUrl);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${appConfig.appFactoryBaseUrl}/health`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const text = await res.text();
      return {
        ok: res.ok,
        status: res.status,
        bodyPreview: text.slice(0, 240),
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`产线 /health 超时（${appConfig.appFactoryBaseUrl}/health）`);
      }
      throw new Error(
        `无法连接产线 /health ${appConfig.appFactoryBaseUrl}（${error instanceof Error ? error.message : String(error)}）`,
      );
    } finally {
      clearTimeout(timer);
    }
  },

  async createOrder(input: {
    title?: string;
    note?: string;
    preferredWorkerUserId: string;
    preferredWorkerAccountId: string;
  }): Promise<FactoryOrder> {
    const res = await factoryFetch<{ success: boolean; order: FactoryOrder }>('/api/orders', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return res.order;
  },

  async listMyOrders(): Promise<FactoryOrder[]> {
    const res = await factoryFetch<{ success: boolean; orders: FactoryOrder[]; history?: FactoryOrder[] }>(
      '/api/orders/mine',
    );
    return (res.orders || []).filter((o) => !['cancelled', 'closed'].includes(o.status));
  },

  async listMyOrderHistory(): Promise<FactoryOrder[]> {
    const res = await factoryFetch<{ success: boolean; orders: FactoryOrder[]; history?: FactoryOrder[] }>(
      '/api/orders/mine',
    );
    if (Array.isArray(res.history)) return res.history;
    return (res.orders || []).filter((o) => ['cancelled', 'closed'].includes(o.status));
  },

  async listMyOrdersWithHistory(): Promise<{ orders: FactoryOrder[]; history: FactoryOrder[] }> {
    const res = await factoryFetch<{ success: boolean; orders: FactoryOrder[]; history?: FactoryOrder[] }>(
      '/api/orders/mine',
    );
    const orders = (res.orders || []).filter((o) => !['cancelled', 'closed'].includes(o.status));
    const history = Array.isArray(res.history)
      ? res.history
      : (res.orders || []).filter((o) => ['cancelled', 'closed'].includes(o.status));
    return { orders, history };
  },

  async getOrderReleasePreview(orderId: string): Promise<FactoryOrderReleasePreview> {
    const res = await factoryFetch<{
      success: boolean;
      order: FactoryOrder;
      products: FactoryReleasePreviewProduct[];
      summary: FactoryOrderReleasePreview['summary'];
    }>(`/api/orders/${encodeURIComponent(orderId)}/release-preview`);
    return {
      order: res.order,
      products: res.products || [],
      summary: res.summary || { productCount: 0, particleCount: 0, batchCount: 0 },
    };
  },

  async listFactoryOrders(): Promise<FactoryOrder[]> {
    const res = await factoryFetch<{ success: boolean; orders: FactoryOrder[] }>('/api/orders');
    return res.orders || [];
  },

  async claimOrder(orderId: string): Promise<FactoryOrder> {
    const res = await factoryFetch<{ success: boolean; order: FactoryOrder }>(
      `/api/orders/${encodeURIComponent(orderId)}/claim`,
      { method: 'POST', body: '{}' },
    );
    return res.order;
  },

  async cancelOrder(orderId: string): Promise<FactoryOrder> {
    const res = await factoryFetch<{ success: boolean; order: FactoryOrder }>(
      `/api/orders/${encodeURIComponent(orderId)}/cancel`,
      { method: 'POST', body: '{}' },
    );
    return res.order;
  },

  async abandonOrder(orderId: string): Promise<FactoryOrder> {
    const res = await factoryFetch<{ success: boolean; order: FactoryOrder }>(
      `/api/orders/${encodeURIComponent(orderId)}/abandon`,
      { method: 'POST', body: '{}' },
    );
    return res.order;
  },

  async requestPageSupplement(
    orderId: string,
    billedPageCount: number,
  ): Promise<{ billedPageCount: number; extraPages: number; supplementFen: number }> {
    const res = await factoryFetch<{
      success: boolean;
      supplement: { billedPageCount: number; extraPages: number; supplementFen: number };
    }>(`/api/orders/${encodeURIComponent(orderId)}/page-supplement`, {
      method: 'POST',
      body: JSON.stringify({ billedPageCount }),
    });
    return res.supplement;
  },

  async listBatches(): Promise<FactoryBatch[]> {
    const res = await factoryFetch<{ success: boolean; batches: FactoryBatch[] }>('/api/batches');
    return res.batches || [];
  },

  async listShelf(options?: { timeoutMs?: number }): Promise<FactoryShelfMaterial[]> {
    const res = await factoryFetch<{ success: boolean; materials: FactoryShelfMaterial[] }>(
      '/api/shelf',
      {},
      options?.timeoutMs ?? 15_000,
    );
    return res.materials || [];
  },

  async uploadToShelf(
    file: File,
    options?: {
      orderId?: string;
      materialCode?: string;
      onProgress?: (pct: number) => void;
      signal?: AbortSignal;
    },
  ): Promise<FactoryShelfMaterial> {
    const token = await apiClient.getAccessToken();
    if (!token) throw new Error('未登录，无法上传');
    const orderId = String(options?.orderId || '').trim();
    const materialCode = String(options?.materialCode || '').trim();
    if (!orderId && !materialCode) throw new Error('请选择工厂订单（或填写素材码）');
    const onProgress = options?.onProgress;
    const signal = options?.signal;
    if (signal?.aborted) throw new Error('已取消上传');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', onAbort);
        fn();
      };
      const onAbort = () => {
        xhr.abort();
        finish(() => reject(new Error('已取消上传')));
      };
      signal?.addEventListener('abort', onAbort);

      xhr.open('POST', `${appConfig.appFactoryBaseUrl}/api/shelf/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.timeout = 30 * 60 * 1000;
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      };
      xhr.onload = () => {
        try {
          const payload = JSON.parse(xhr.responseText || '{}') as Record<string, unknown>;
          if (xhr.status >= 200 && xhr.status < 300) {
            finish(() => resolve(payload.material as FactoryShelfMaterial));
          } else {
            finish(() => reject(new Error(friendlyError(payload, xhr.status))));
          }
        } catch (err) {
          finish(() => reject(err instanceof Error ? err : new Error(String(err))));
        }
      };
      xhr.onerror = () => finish(() => reject(new Error('网络错误，无法上架到颗粒工厂')));
      xhr.ontimeout = () => finish(() => reject(new Error('上传超时')));
      xhr.onabort = () => finish(() => reject(new Error('已取消上传')));
      const form = new FormData();
      form.append('file', file);
      // Explicit UTF-8 field — multipart Content-Disposition filename is often latin1-mojibake.
      form.append('clientFilename', file.name || 'upload.pdf');
      if (orderId) form.append('orderId', orderId);
      if (materialCode) form.append('materialCode', materialCode);
      xhr.send(form);
    });
  },

  async listOwnerPendingBatches(): Promise<FactoryBatch[]> {
    const res = await factoryFetch<{ success: boolean; batches: FactoryBatch[] }>(
      '/api/owner/batches/pending-auth',
    );
    return res.batches || [];
  },

  async listOwnerPendingReleaseBatches(): Promise<FactoryBatch[]> {
    const res = await factoryFetch<{ success: boolean; batches: FactoryBatch[] }>(
      '/api/owner/batches/pending-release',
    );
    return res.batches || [];
  },

  async authorizeOwnerBatch(
    batchId: string,
    decision: 'authorize_factory' | 'authorize_self' | 'reject',
  ): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/owner/batches/${encodeURIComponent(batchId)}/authorize`,
      {
        method: 'POST',
        body: JSON.stringify({ decision }),
      },
    );
    return res.batch;
  },

  async authorizeOwnerRelease(
    batchId: string,
    decision: 'authorize_factory' | 'reject' | 'start' | 'stop',
  ): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/owner/batches/${encodeURIComponent(batchId)}/authorize-release`,
      {
        method: 'POST',
        body: JSON.stringify({
          decision:
            decision === 'authorize_factory'
              ? 'start'
              : decision === 'reject'
                ? 'stop'
                : decision,
        }),
      },
    );
    return res.batch;
  },

  async acceptOwnerGoods(batchId: string): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/owner/batches/${encodeURIComponent(batchId)}/accept-goods`,
      { method: 'POST', body: '{}' },
    );
    return res.batch;
  },

  async bindMaterialCode(code: string, accountId: string, userId?: string): Promise<void> {
    await factoryFetch(`/api/material-codes/${encodeURIComponent(code)}/bind`, {
      method: 'POST',
      body: JSON.stringify({ accountId, userId }),
    });
  },

  async createMaterialCode(code: string): Promise<void> {
    await factoryFetch('/api/material-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async deleteShelfMaterial(id: string): Promise<void> {
    await factoryFetch<{ success: boolean }>(`/api/shelf/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  async countShelfPages(id: string): Promise<FactoryShelfMaterial> {
    const res = await factoryFetch<{
      success: boolean;
      material: FactoryShelfMaterial;
      pageCount?: number;
    }>(
      `/api/shelf/${encodeURIComponent(id)}/count-pages`,
      { method: 'POST', body: '{}' },
      90_000,
    );
    return res.material;
  },

  async admitShelfMaterial(id: string): Promise<{ batch: FactoryBatch; job: FactoryJob | null }> {
    const res = await factoryFetch<{
      success: boolean;
      batch: FactoryBatch;
      job: FactoryJob | null;
    }>(`/api/shelf/${encodeURIComponent(id)}/admit`, {
      method: 'POST',
      body: '{}',
    });
    return { batch: res.batch, job: res.job || null };
  },

  async createBatch(title: string, strategyTier?: string): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>('/api/batches', {
      method: 'POST',
      body: JSON.stringify({ title, strategyTier }),
    });
    return res.batch;
  },

  async getBatch(id: string): Promise<FactoryBatchDetail> {
    return factoryFetch<FactoryBatchDetail>(`/api/batches/${encodeURIComponent(id)}`);
  },

  async getBatchUsage(id: string): Promise<{
    batchId: string;
    title: string;
    status: string;
    processingAuthorization?: string;
    createdAt?: string;
    updatedAt?: string;
    meta?: Record<string, unknown>;
    usage: {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
      byPhase: Record<string, {
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
      }>;
      phaseMetrics?: FactoryPhaseMetric[];
      notes: string[];
    };
  }> {
    return factoryFetch(`/api/batches/${encodeURIComponent(id)}/usage`);
  },

  async deleteBatch(id: string): Promise<void> {
    await factoryFetch<{ success: boolean }>(`/api/batches/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  async enqueueLayout(id: string): Promise<FactoryJob> {
    const res = await factoryFetch<{ success: boolean; job: FactoryJob }>(
      `/api/batches/${encodeURIComponent(id)}/enqueue-layout`,
      { method: 'POST', body: '{}' },
    );
    return res.job;
  },

  async setBatchPipelineOptions(
    id: string,
    options: { enableSemanticSplit: boolean },
  ): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/batches/${encodeURIComponent(id)}/options`,
      {
        method: 'PATCH',
        body: JSON.stringify(options),
      },
    );
    return res.batch;
  },

  /** Re-sample document blocks → judge-block-subject (AppFactory proxies FC). */
  async judgeDiscipline(
    id: string,
    options?: { maxBlocks?: number },
  ): Promise<{ batch: FactoryBatch; discipline: Record<string, unknown>; enqueued: string[] }> {
    const res = await factoryFetch<{
      success: boolean;
      batch: FactoryBatch;
      discipline: Record<string, unknown>;
      enqueued?: string[];
    }>(`/api/batches/${encodeURIComponent(id)}/judge-discipline`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
    return {
      batch: res.batch,
      discipline: res.discipline || {},
      enqueued: res.enqueued || [],
    };
  },

  async restartPipeline(
    id: string,
    options?: { enableSemanticSplit?: boolean },
  ): Promise<FactoryJob> {
    const res = await factoryFetch<{ success: boolean; job: FactoryJob }>(
      `/api/batches/${encodeURIComponent(id)}/restart-pipeline`,
      {
        method: 'POST',
        body: JSON.stringify(options || {}),
      },
    );
    return res.job;
  },

  /** Resume extract without wiping succeeded units (unless forceRedo). */
  async continueExtract(id: string, options?: { forceRedo?: boolean }): Promise<FactoryJob> {
    const res = await factoryFetch<{ success: boolean; job: FactoryJob }>(
      `/api/batches/${encodeURIComponent(id)}/continue-extract`,
      { method: 'POST', body: JSON.stringify(options || {}) },
    );
    return res.job;
  },

  /** Checkpoint continue: abort open jobs, enqueue earliest incomplete phase. */
  async continuePipeline(id: string): Promise<{
    job: FactoryJob;
    note: string;
    phase: string;
    phaseLabel: string;
    enqueued: string;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      job: FactoryJob;
      note?: string;
      phase?: string;
      phaseLabel?: string;
      enqueued?: string;
    }>(`/api/batches/${encodeURIComponent(id)}/continue-pipeline`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return {
      job: res.job,
      note: res.note || '已从检查点继续',
      phase: res.phase || '',
      phaseLabel: res.phaseLabel || '',
      enqueued: res.enqueued || '',
    };
  },

  /** Drain in-flight FC for this batch, then stop taking new concurrent calls. */
  async pausePipeline(id: string): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/batches/${encodeURIComponent(id)}/pause`,
      { method: 'POST', body: JSON.stringify({}) },
    );
    return res.batch;
  },

  /** Resume a cooperatively paused batch (does not abort in-flight work). */
  async resumePipeline(id: string): Promise<FactoryBatch> {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/batches/${encodeURIComponent(id)}/resume`,
      { method: 'POST', body: JSON.stringify({}) },
    );
    return res.batch;
  },

  async listLayoutUnits(batchId: string): Promise<FactoryLayoutUnit[]> {
    const res = await factoryFetch<{ success: boolean; units: FactoryLayoutUnit[] }>(
      `/api/batches/${encodeURIComponent(batchId)}/layout-units`,
    );
    return res.units || [];
  },

  async listSemanticSplits(batchId: string): Promise<FactorySemanticSplitsResult> {
    const res = await factoryFetch<{
      success: boolean;
      pages: FactorySemanticSplitPage[];
      summary: FactorySemanticSplitsResult['summary'];
    }>(`/api/batches/${encodeURIComponent(batchId)}/semantic-splits`);
    return {
      pages: res.pages || [],
      summary: res.summary || {
        pageCount: 0,
        lineCount: 0,
        model: '',
        totalPromptTokens: 0,
        totalCompletionTokens: 0,
        totalLatencyMs: 0,
      },
    };
  },

  semanticSplitImageUrl(batchId: string, pageIndex: number): string {
    return `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/semantic-splits/${encodeURIComponent(String(pageIndex))}/image`;
  },

  async getLayoutUnitContent(
    batchId: string,
    unitId: string,
  ): Promise<{ contentItems: FactoryLayoutContentItem[]; missing?: boolean }> {
    const res = await factoryFetch<{
      success: boolean;
      contentItems: FactoryLayoutContentItem[];
      missing?: boolean;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/layout-units/${encodeURIComponent(unitId)}/layout`,
    );
    return { contentItems: res.contentItems || [], missing: res.missing };
  },

  layoutUnitPdfUrl(batchId: string, unitId: string): string {
    return `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/layout-units/${encodeURIComponent(unitId)}/pdf`;
  },

  /** Single-page JPEG for compare desk (Bearer via AuthImage). */
  structuralUnitPageImageUrl(batchId: string, unitId: string): string {
    return `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/structural-units/${encodeURIComponent(unitId)}/page-image`;
  },

  layoutUnitAssetUrl(batchId: string, unitId: string, relPath: string): string {
    const q = new URLSearchParams({ path: relPath });
    return `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/layout-units/${encodeURIComponent(unitId)}/asset?${q}`;
  },

  layoutUnitCropUrl(
    batchId: string,
    unitId: string,
    pageIdx: number,
    bbox: [number, number, number, number],
  ): string {
    const q = new URLSearchParams({
      page_idx: String(pageIdx),
      bbox: bbox.join(','),
    });
    return `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/layout-units/${encodeURIComponent(unitId)}/crop?${q}`;
  },

  assetPdfUrl(batchId: string, assetId: string): string {
    return `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/assets/${encodeURIComponent(assetId)}/pdf`;
  },

  async listStructuralUnits(
    batchId: string,
    options?: { view?: 'preview' | 'full' },
  ): Promise<FactoryStructuralUnit[]> {
    const view = options?.view || 'full';
    const q = view === 'preview' ? `?view=${encodeURIComponent(view)}` : '';
    const res = await factoryFetch<{ success: boolean; units: FactoryStructuralUnit[] }>(
      `/api/batches/${encodeURIComponent(batchId)}/structural-units${q}`,
    );
    return res.units || [];
  },

  /** Lazy full extract/enrich payload for one unit. */
  async getStructuralUnit(
    batchId: string,
    unitId: string,
  ): Promise<FactoryStructuralUnit> {
    const res = await factoryFetch<{ success: boolean; unit: FactoryStructuralUnit }>(
      `/api/batches/${encodeURIComponent(batchId)}/structural-units/${encodeURIComponent(unitId)}`,
    );
    return res.unit;
  },

  /** Warehouse 品题入围 inventory (Contracts-shaped recommendations + draft TTQAs). */
  async getTtqAdmission(batchId: string): Promise<FactoryTtqAdmissionResponse> {
    return factoryFetch<FactoryTtqAdmissionResponse>(
      `/api/batches/${encodeURIComponent(batchId)}/ttq-admission?view=summary`,
    );
  },

  /** Full particle Q/A for one warehouse recommendation group (drawer). */
  async getTtqAdmissionGroup(
    batchId: string,
    ttqaId: string,
  ): Promise<{ warehouseTtqa: FactoryWarehouseTtqa }> {
    const res = await factoryFetch<{
      success: boolean;
      warehouseTtqa: FactoryWarehouseTtqa;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/ttq-admission/groups/${encodeURIComponent(ttqaId)}`,
    );
    return { warehouseTtqa: res.warehouseTtqa };
  },

  /** Re-run full 品题入围 clustering + Flash title condense (no drip; chains ipttq). */
  async rerunTtqAdmission(batchId: string): Promise<{ job: FactoryJob; message?: string }> {
    const res = await factoryFetch<{
      success: boolean;
      job: FactoryJob;
      message?: string;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/rerun-ttq-admission`,
      { method: 'POST', body: '{}' },
    );
    return { job: res.job, message: res.message };
  },

  /**
   * Fuse small/orphan warehouse recommendation groups into peer clusters
   * (similarity re-admit only; keeps existing ipttq).
   */
  async mergeTtqOrphanGroups(batchId: string): Promise<{
    message?: string;
    merged: number;
    considered: number;
    ttqAdmission: FactoryTtqAdmissionResult | null;
    builtAt?: string | null;
    report?: SimilarityReAdmitReport | null;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      message?: string;
      merged?: number;
      considered?: number;
      ttqAdmission?: FactoryTtqAdmissionResult | null;
      builtAt?: string | null;
      report?: SimilarityReAdmitReport | null;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/ttq-admission/merge-orphans`,
      { method: 'POST', body: '{}' },
    );
    return {
      message: res.message,
      merged: Number(res.merged) || 0,
      considered: Number(res.considered) || 0,
      ttqAdmission: res.ttqAdmission || null,
      builtAt: res.builtAt || null,
      report: res.report || null,
    };
  },

  /** Desk drag-drop: merge source warehouse group into target, refresh ipttq target. */
  async manualMergeTtqGroups(
    batchId: string,
    sourceTtqaId: string,
    targetTtqaId: string,
  ): Promise<{
    message?: string;
    ttqAdmission: FactoryTtqAdmissionResult | null;
    bankTarget: number | null;
    ipttqJobId: string | null;
    particlesMoved: number;
    canUndo: boolean;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      message?: string;
      ttqAdmission?: FactoryTtqAdmissionResult | null;
      bankTarget?: number | null;
      ipttqJobId?: string | null;
      particlesMoved?: number;
      canUndo?: boolean;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/ttq-admission/manual-merge`,
      {
        method: 'POST',
        body: JSON.stringify({ sourceTtqaId, targetTtqaId }),
      },
    );
    return {
      message: res.message,
      ttqAdmission: res.ttqAdmission || null,
      bankTarget: res.bankTarget != null ? Number(res.bankTarget) : null,
      ipttqJobId: res.ipttqJobId || null,
      particlesMoved: Number(res.particlesMoved) || 0,
      canUndo: res.canUndo !== false,
    };
  },

  /** Undo last desk manual-merge. */
  async undoManualMergeTtqGroups(batchId: string): Promise<{
    message?: string;
    ttqAdmission: FactoryTtqAdmissionResult | null;
    bankTarget: number | null;
    ipttqJobId: string | null;
    canUndo: boolean;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      message?: string;
      ttqAdmission?: FactoryTtqAdmissionResult | null;
      bankTarget?: number | null;
      ipttqJobId?: string | null;
      canUndo?: boolean;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/ttq-admission/manual-merge/undo`,
      { method: 'POST', body: '{}' },
    );
    return {
      message: res.message,
      ttqAdmission: res.ttqAdmission || null,
      bankTarget: res.bankTarget != null ? Number(res.bankTarget) : null,
      ipttqJobId: res.ipttqJobId || null,
      canUndo: Boolean(res.canUndo),
    };
  },

  /**
   * Idempotent: write existing 入围仓 into durable 成品智能体仓 (join / create / critical).
   * Skips rows already stamped with disposition.
   */
  async resolveTtqProducts(batchId: string): Promise<{
    ttqAdmission: FactoryTtqAdmissionResult | null;
    productSummary: {
      created: number;
      joined: number;
      critical: number;
      productCount: number;
      groupCount: number;
      unresolved: number;
    };
    builtAt?: string | null;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      ttqAdmission: FactoryTtqAdmissionResult | null;
      productSummary: {
        created: number;
        joined: number;
        critical: number;
        productCount: number;
        groupCount: number;
        unresolved: number;
      };
      builtAt?: string | null;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/resolve-ttq-products`,
      { method: 'POST', body: '{}' },
    );
    return {
      ttqAdmission: res.ttqAdmission,
      productSummary: res.productSummary,
      builtAt: res.builtAt,
    };
  },

  /** Reject pending critical for groups already created/joined (历史残留). */
  async cleanupStaleTtqCritical(batchId: string): Promise<{
    rejected: number;
    archived?: number;
    message?: string;
    keepProductIds?: string[];
    warehouseGroupCount?: number;
  }> {
    return factoryFetch(`/api/batches/${encodeURIComponent(batchId)}/ttq-critical-stale-cleanup`, {
      method: 'POST',
      body: '{}',
    });
  },

  /** Warehouse 品题浓缩 + 诱导题库草稿（Contracts IpttqRecord 同形；不滴灌）. */
  async getIpttq(batchId: string): Promise<FactoryIpttqResponse> {
    return factoryFetch<FactoryIpttqResponse>(
      `/api/batches/${encodeURIComponent(batchId)}/ipttq`,
    );
  },

  /** Re-run or continue 品题浓缩 + bank drafts（requires ttqAdmission; no ISK; no drip）. */
  async rerunIpttq(
    batchId: string,
    options?: { mode?: 'full' | 'continue' },
  ): Promise<{ job: FactoryJob; message?: string }> {
    const mode = options?.mode === 'continue' ? 'continue' : 'full';
    const res = await factoryFetch<{
      success: boolean;
      job: FactoryJob;
      message?: string;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/rerun-ipttq`,
      { method: 'POST', body: JSON.stringify({ mode }) },
    );
    return { job: res.job, message: res.message };
  },

  /** Force re-extract one structural unit / page (sync; math → math-k12-extra). */
  async reextractStructuralUnit(
    batchId: string,
    unitId: string,
    opts?: { particlesOnly?: boolean },
  ): Promise<{
    ok: boolean;
    qaCount: number;
    extractTarget: string;
    error?: string | null;
    message?: string;
    unit?: Partial<FactoryStructuralUnit> | null;
    particlesOnly?: boolean;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      result?: {
        ok: boolean;
        qaCount: number;
        extractTarget: string;
        error?: string | null;
        particlesOnly?: boolean;
      };
      unit?: Partial<FactoryStructuralUnit> | null;
      message?: string;
      error?: string;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/structural-units/${encodeURIComponent(unitId)}/reextract`,
      {
        method: 'POST',
        body: JSON.stringify({
          particlesOnly: opts?.particlesOnly === true,
        }),
      },
      20 * 60 * 1000,
    );
    return {
      ok: res.result?.ok ?? res.success,
      qaCount: res.result?.qaCount ?? 0,
      extractTarget: res.result?.extractTarget || '',
      error: res.result?.error,
      message: res.message,
      unit: res.unit,
      particlesOnly: res.result?.particlesOnly === true,
    };
  },

  /** Force re-enrich one unit (RFE+PAP only; keeps extract QA). */
  async reenrichStructuralUnit(
    batchId: string,
    unitId: string,
  ): Promise<{
    ok: boolean;
    qaCount: number;
    rfeBound: number;
    papReady: number;
    error?: string | null;
    message?: string;
    unit?: Partial<FactoryStructuralUnit> | null;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      result?: {
        ok: boolean;
        qaCount: number;
        rfeBound: number;
        papReady: number;
        error?: string | null;
      };
      unit?: Partial<FactoryStructuralUnit> | null;
      message?: string;
      error?: string;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/structural-units/${encodeURIComponent(unitId)}/reenrich`,
      { method: 'POST', body: '{}' },
      10 * 60 * 1000,
    );
    return {
      ok: res.result?.ok ?? res.success,
      qaCount: res.result?.qaCount ?? 0,
      rfeBound: res.result?.rfeBound ?? 0,
      papReady: res.result?.papReady ?? 0,
      error: res.result?.error,
      message: res.message,
      unit: res.unit,
    };
  },

  /** Ignore / un-ignore extract for one structural unit (blank pages). */
  async setStructuralUnitExtractIgnored(
    batchId: string,
    unitId: string,
    ignore: boolean,
  ): Promise<{
    ignored: boolean;
    message?: string;
    unit?: Partial<FactoryStructuralUnit> | null;
    batch?: FactoryBatch | null;
    counts?: { succeeded: number; failed: number; ignored: number; unitCount: number };
  }> {
    const res = await factoryFetch<{
      success: boolean;
      ignored?: boolean;
      message?: string;
      unit?: Partial<FactoryStructuralUnit> | null;
      batch?: FactoryBatch | null;
      counts?: { succeeded: number; failed: number; ignored: number; unitCount: number };
      error?: string;
    }>(
      `/api/batches/${encodeURIComponent(batchId)}/structural-units/${encodeURIComponent(unitId)}/ignore-extract`,
      { method: 'POST', body: JSON.stringify({ ignore }) },
    );
    return {
      ignored: res.ignored ?? ignore,
      message: res.message,
      unit: res.unit,
      batch: res.batch || null,
      counts: res.counts,
    };
  },

  async getAccessToken(): Promise<string | undefined> {
    return apiClient.getAccessToken();
  },

  async uploadAsset(
    batchId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ asset: FactoryAsset; job: FactoryJob | null }> {
    const token = await apiClient.getAccessToken();
    if (!token) throw new Error('未登录，无法上传');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${appConfig.appFactoryBaseUrl}/api/batches/${encodeURIComponent(batchId)}/assets/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.timeout = 30 * 60 * 1000;
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      };
      xhr.onload = () => {
        try {
          const payload = JSON.parse(xhr.responseText || '{}') as Record<string, unknown>;
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              asset: payload.asset as FactoryAsset,
              job: (payload.job as FactoryJob) || null,
            });
          } else {
            reject(new Error(friendlyError(payload, xhr.status)));
          }
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      };
      xhr.onerror = () => reject(new Error('网络错误，无法上传到颗粒工厂'));
      xhr.ontimeout = () => reject(new Error('上传超时'));
      const form = new FormData();
      form.append('file', file);
      form.append('clientFilename', file.name || 'upload.pdf');
      xhr.send(form);
    });
  },

  async getWorkerQueue() {
    return factoryFetch<{
      success: boolean;
      queue: FactoryWorkerQueueStatus;
    }>('/api/worker/queue');
  },

  async getMinerUSettings() {
    return factoryFetch<{
      success: boolean;
      mineru: {
        apiBase: string;
        modelVersion: string;
        hasToken: boolean;
        apiTokenMasked: string;
      };
    }>('/api/settings/mineru');
  },

  async updateMinerUSettings(body: {
    apiBase?: string;
    modelVersion?: string;
    apiToken?: string;
  }) {
    return factoryFetch<{
      success: boolean;
      mineru: {
        apiBase: string;
        modelVersion: string;
        hasToken: boolean;
        apiTokenMasked: string;
      };
    }>('/api/settings/mineru', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getEnrichSettings(): Promise<{
    edgeBase: string;
    edgeBaseDefault: string;
    hasAdminToken: boolean;
    adminTokenMasked: string;
    adminTokenSource: 'settings' | 'env' | 'none' | string;
    hint?: string;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      enrich: {
        edgeBase: string;
        edgeBaseDefault: string;
        hasAdminToken: boolean;
        adminTokenMasked: string;
        adminTokenSource: string;
        hint?: string;
      };
    }>('/api/settings/enrich');
    return res.enrich;
  },

  async updateEnrichSettings(body: {
    adminToken?: string;
    edgeBase?: string;
    clearAdminToken?: boolean;
  }) {
    const res = await factoryFetch<{
      success: boolean;
      enrich: {
        edgeBase: string;
        edgeBaseDefault: string;
        hasAdminToken: boolean;
        adminTokenMasked: string;
        adminTokenSource: string;
        hint?: string;
      };
    }>('/api/settings/enrich', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return res.enrich;
  },

  async getPipelineSettings(): Promise<{
    autoRequestOwnerAcceptIfCleanExtract: boolean;
  }> {
    const res = await factoryFetch<{
      success: boolean;
      pipeline: { autoRequestOwnerAcceptIfCleanExtract: boolean };
    }>('/api/settings/pipeline');
    return res.pipeline;
  },

  async updatePipelineSettings(body: {
    autoRequestOwnerAcceptIfCleanExtract: boolean;
  }) {
    return factoryFetch<{
      success: boolean;
      pipeline: { autoRequestOwnerAcceptIfCleanExtract: boolean };
    }>('/api/settings/pipeline', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getCostLedger(): Promise<FactoryCostLedger> {
    const res = await factoryFetch<{
      success: boolean;
      ledger: FactoryCostLedger;
    }>('/api/settings/cost-ledger');
    return res.ledger;
  },

  async requestOwnerAccept(batchId: string): Promise<{
    batch: FactoryBatch;
    message?: string;
  }> {
    return factoryFetch(`/api/batches/${encodeURIComponent(batchId)}/request-owner-accept`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  async listGoods(): Promise<FactoryBatch[]> {
    const res = await factoryFetch<{ success: boolean; batches: FactoryBatch[] }>('/api/goods');
    return res.batches || [];
  },

  async listTtqProducts(params?: {
    subjectCode?: 'math' | 'yuwen';
    ownedByUserId?: string;
    sourceBatchId?: string;
  }): Promise<FactoryTtqProduct[]> {
    const q = new URLSearchParams();
    if (params?.subjectCode) q.set('subjectCode', params.subjectCode);
    if (params?.ownedByUserId) q.set('ownedByUserId', params.ownedByUserId);
    if (params?.sourceBatchId) q.set('sourceBatchId', params.sourceBatchId);
    const suffix = q.toString() ? `?${q}` : '';
    const res = await factoryFetch<{ success: boolean; products: FactoryTtqProduct[] }>(
      `/api/ttq-products${suffix}`,
    );
    return res.products || [];
  },

  async getTtqProduct(
    productId: string,
    options?: { includeMembers?: boolean },
  ): Promise<{
    product: FactoryTtqProduct;
    members: FactoryTtqProductMember[];
  }> {
    const includeMembers = options?.includeMembers !== false;
    const q = includeMembers ? '' : '?includeMembers=0';
    return factoryFetch(`/api/ttq-products/${encodeURIComponent(productId)}${q}`);
  },

  async listTtqProductMembers(productId: string): Promise<{
    productId: string;
    title: string;
    memberCount: number;
    members: FactoryTtqProductMember[];
  }> {
    return factoryFetch(`/api/ttq-products/${encodeURIComponent(productId)}/members`);
  },

  async patchTtqProduct(
    productId: string,
    body: { memberCap?: number; title?: string; inductionBankMemberRatio?: number },
  ): Promise<{ product: FactoryTtqProduct; message?: string }> {
    return factoryFetch(`/api/ttq-products/${encodeURIComponent(productId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async startProductBankStock(productId: string): Promise<{
    success: boolean;
    started: boolean;
    run: ProductBankStockRunState;
    bankCount: number;
  }> {
    return factoryFetch(`/api/ttq-products/${encodeURIComponent(productId)}/generate-bank-stock`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  async pauseProductBankStock(productId: string): Promise<{
    success: boolean;
    paused: boolean;
    run: ProductBankStockRunState | null;
    bankCount: number;
  }> {
    return factoryFetch(`/api/ttq-products/${encodeURIComponent(productId)}/generate-bank-stock`, {
      method: 'DELETE',
    });
  },

  async getProductBankStockStatus(productId: string): Promise<ProductBankStockStatusResponse> {
    return factoryFetch(`/api/ttq-products/${encodeURIComponent(productId)}/generate-bank-stock`);
  },

  async listTtqCriticalZone(params?: {
    status?: 'pending' | 'approved' | 'rejected';
    ownedByUserId?: string;
  }): Promise<FactoryCriticalZoneItem[]> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.ownedByUserId) q.set('ownedByUserId', params.ownedByUserId);
    const suffix = q.toString() ? `?${q}` : '';
    const res = await factoryFetch<{ success: boolean; items: FactoryCriticalZoneItem[] }>(
      `/api/ttq-critical-zone${suffix}`,
    );
    return res.items || [];
  },

  async decideTtqCriticalZone(
    id: string,
    decision: 'approve' | 'reject',
  ): Promise<{
    item: FactoryCriticalZoneItem;
    product?: FactoryTtqProduct;
    message?: string;
    ttqAdmission?: FactoryTtqAdmissionResult | null;
  }> {
    return factoryFetch(`/api/ttq-critical-zone/${encodeURIComponent(id)}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    });
  },

  async decideTtqCriticalZoneBulk(
    ids: string[],
    decision: 'approve' | 'reject',
  ): Promise<{
    ok: number;
    total: number;
    errors: Array<{ id: string; error: string }>;
    message?: string;
    ttqAdmission?: FactoryTtqAdmissionResult | null;
  }> {
    return factoryFetch('/api/ttq-critical-zone/decide-bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, decision }),
    });
  },

  async updateWarehouseStatus(batchId: string, warehouseStatus: string, qcNote?: string) {
    const res = await factoryFetch<{ success: boolean; batch: FactoryBatch }>(
      `/api/goods/${encodeURIComponent(batchId)}/warehouse`,
      {
        method: 'POST',
        body: JSON.stringify({ warehouseStatus, qcNote }),
      },
    );
    return res.batch;
  },

  async listReleaseRequests(): Promise<FactoryReleaseRequest[]> {
    const res = await factoryFetch<{ success: boolean; requests: FactoryReleaseRequest[] }>(
      '/api/release-requests',
    );
    return res.requests || [];
  },

  async submitRelease(batchId: string): Promise<FactoryReleaseRequest> {
    const res = await factoryFetch<{ success: boolean; request: FactoryReleaseRequest }>(
      '/api/release-requests',
      {
        method: 'POST',
        body: JSON.stringify({ batchId }),
      },
    );
    return res.request;
  },

  async recallRelease(id: string): Promise<FactoryReleaseRequest> {
    const res = await factoryFetch<{ success: boolean; request: FactoryReleaseRequest }>(
      `/api/release-requests/${encodeURIComponent(id)}/recall`,
      { method: 'POST', body: '{}' },
    );
    return res.request;
  },
};
